import { useLayoutEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import {
  ApiPaymentMethods,
  confirmedPaymentRoutes,
  errorRoutes,
  PaymentMethodKeys,
  ResponseStatus,
  RoutesPath,
  waitingPaymentRoutes,
} from "src/constants";
import {
  getPaymentMethodValidations,
  getStatusCheck,
  isAValidRoute,
  NavigationHandlers,
} from "src/lib/navigation";
import { isInstallmentRequired } from "src/utils";

const useNavigateCheckout = (data: CheckoutType) => {
  const params = useParams<OrderSubscriptionParamsType>();

  const token = params.token as string;
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useLayoutEffect(() => {
    if (!data) {
      return;
    }

    const {
      paymentMethod,
      status,
      isPaymentScheduled,
      isPendingStatus,
      isConfirmedStatusAndPaymentMethodRoute,
      isPreAuthorizationErrorWithoutPaymentMethod,
      isPaymentMethodSelectionRoute,
      isWaitingPaymentRoute,
      isPreAuthorizationErrorStatus,
      isPaymentConfirmedLongTimeAgo,
      isFutureStatus,
    } = getStatusCheck(data, pathname, token);

    const paymentMethodValidations = getPaymentMethodValidations(
      pathname,
      token,
      data,
    );
    if (!isAValidRoute(pathname)) {
      void navigate(`${RoutesPath.HOME}`);
      return;
    }

    const activePaymentMethodType = (data.subscriptionBillingCycle?.subscription
      ?.paymentMethodActive?.type ??
      data.checkoutPreferences?.paymentMethods?.[0]?.type) as
      keyof typeof PaymentMethodKeys | undefined;
    const currentPaymentMethod = activePaymentMethodType
      ? PaymentMethodKeys[activePaymentMethodType]
      : undefined;

    if (
      data.changePaymentMethod &&
      (activePaymentMethodType === ApiPaymentMethods.CREDIT_CARD ||
        activePaymentMethodType === ApiPaymentMethods.DIRECT_DEBIT) &&
      currentPaymentMethod?.route &&
      (pathname === `/${token}` ||
        pathname === `/${token}${RoutesPath.PAYMENT_METHODS}`)
    ) {
      void navigate(`/${token}${currentPaymentMethod.route}`);
      return;
    }

    if (isFutureStatus) {
      if (!isPaymentScheduled) {
        NavigationHandlers.handleInvalidPaymentAccess(
          paymentMethodValidations,
          navigate,
          token,
        );
        return;
      }

      return;
    }

    if (isPendingStatus) {
      if (
        data.changePaymentMethod &&
        currentPaymentMethod?.route &&
        (pathname === `/${token}` ||
          pathname === `/${token}${RoutesPath.PAYMENT_METHODS}`)
      ) {
        const isInstallmentRequiredFlag = isInstallmentRequired(data);

        const isFormRequired =
          isInstallmentRequiredFlag ||
          activePaymentMethodType === ApiPaymentMethods.CREDIT_CARD ||
          activePaymentMethodType === ApiPaymentMethods.DIRECT_DEBIT;

        if (!isFormRequired) {
          return;
        }

        let targetRoute = currentPaymentMethod.route;

        if (isInstallmentRequiredFlag) {
          if (
            activePaymentMethodType === ApiPaymentMethods.BANK_SLIP ||
            activePaymentMethodType === ApiPaymentMethods.BOLEPIX
          ) {
            targetRoute = RoutesPath.BANK_SLIP_INSTALLMENTS_SELECTION;
          } else if (activePaymentMethodType === ApiPaymentMethods.PIX) {
            targetRoute = RoutesPath.PIX_INSTALLMENTS_SELECTION;
          }
        }

        void navigate(`/${token}${targetRoute}`);
        return;
      }

      NavigationHandlers.handleInvalidPaymentAccess(
        paymentMethodValidations,
        navigate,
        token,
      );
      return;
    }

    if (isPaymentMethodSelectionRoute && isPreAuthorizationErrorStatus) {
      return;
    }

    if (isPaymentConfirmedLongTimeAgo) {
      void navigate(`/${token}${RoutesPath.PAYMENT_CONFIRMED}`);
      return;
    }

    if (
      isPreAuthorizationErrorWithoutPaymentMethod ||
      status === ResponseStatus.REVERSED_FAILED
    ) {
      void navigate(`/${token}${errorRoutes[status]}`);
      return;
    }

    // Skip auto-navigation to PRE_AUTHORIZATION_WAITING page when on payment method routes
    // This allows the retry logic in usePaymentNavigation to handle the status
    const isOnPaymentFlowRoute =
      pathname.includes(RoutesPath.PAYMENT_METHODS) ||
      pathname.includes(RoutesPath.CREDIT_CARD) ||
      pathname.includes(RoutesPath.PIX) ||
      pathname.includes(RoutesPath.BANK_SLIP) ||
      pathname.includes(RoutesPath.DIRECT_DEBIT) ||
      pathname.includes(RoutesPath.BOLEPIX) ||
      pathname.includes(RoutesPath.OPEN_FINANCE);

    if (
      isWaitingPaymentRoute &&
      status === ResponseStatus.PRE_AUTHORIZATION_WAITING &&
      isOnPaymentFlowRoute
    ) {
      return;
    }

    if (isWaitingPaymentRoute) {
      void navigate(`/${token}${waitingPaymentRoutes[status]}`);
      return;
    }

    if (isConfirmedStatusAndPaymentMethodRoute) {
      void navigate(`/${token}${confirmedPaymentRoutes[paymentMethod]}`);
      return;
    }
  }, [data, navigate, token, pathname]);
};

export default useNavigateCheckout;
